import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../api";
import { getApiHeaders } from "../../utils/helpers";
import { useUser } from "../../context/UserContext";
import MessageToster from "../MessageToster";

export default function RedeemPointsPopover({
  isOpen,
  onClose,
  conversionRate: propConversionRate,
  minRequired: propMinRequired,
  onSuccess,
}) {
  const { userData, fetchUserData } = useUser() || {};
  const [points, setPoints] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const conversionRate =
    propConversionRate ?? (userData?.points_per_usd != null ? Number(userData.points_per_usd) : 500);
  const minRequired =
    propMinRequired ?? (userData?.min_redeem_points != null ? Number(userData.min_redeem_points) : 20000);

  useEffect(() => {
    if (isOpen && typeof fetchUserData === "function") {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPoints("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePointsChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");
    setPoints(val);
    if (error) setError("");
  };

  const handleKeyDown = (e) => {
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ].includes(e.key) ||
      ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase()))
    ) {
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmed = points.trim();
    if (!trimmed) {
      setError("Please enter points to redeem");
      return;
    }

    const num = Number(trimmed);
    if (isNaN(num) || num <= 0) {
      setError("Please enter a valid points amount");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `${BASE_URL}/account/learn_earn_redeem`,
        { amount: num },
        { headers: getApiHeaders() }
      );

      if (
        (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
        res.data?.code !== 400 &&
        res.data?.status !== "fail" &&
        res.data?.status !== "error"
      ) {
        MessageToster(
          "success",
          res.data?.message || res.data?.msg || "Points redeemed successfully!"
        );
        if (onSuccess) onSuccess(num);
        setPoints("");
        setError("");
        onClose();
      } else {
        const errMsg =
          res.data?.message || res.data?.msg || "Failed to redeem points";
        setError(errMsg);
      }
    } catch (err) {
      console.error("Redeem points error:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Something went wrong while redeeming points.";
      setError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="redeem-popover-root">
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{ zIndex: 1050 }}
        onClick={onClose}
      />

      <div
        className="position-absolute end-0 mt-2 shadow-lg"
        style={{
          width: "360px",
          maxWidth: "calc(100vw - 32px)",
          backgroundColor: "var(--bs-secondary-bg)",
          borderRadius: "12px",
          border: "1px solid var(--bs-border-color-translucent)",
          zIndex: 1055,
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between p-3 px-4 border-bottom">
          <h6
            className="mb-0 fw-bold"
            style={{ fontSize: "16px", color: "var(--bs-emphasis-color)" }}
          >
            Redeem Points
          </h6>
          <button
            type="button"
            className="btn btn-link p-0 text-muted shadow-none border-0"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="bx bx-x font-size-22"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="p-4 pt-3">
          <div
            className="p-3 mb-2 rounded-3 text-center font-size-13"
            style={{
              backgroundColor: "rgba(var(--color-info-rgb), 0.08)",
              border: "1px solid rgba(var(--color-info-rgb), 0.2)",
              color: "var(--bs-emphasis-color)",
            }}
          >
            1 USD = <strong className="fw-bold">{conversionRate}</strong> Points
          </div>

          <div
            className="p-3 mb-3 rounded-3 text-center font-size-13"
            style={{
              backgroundColor: "rgba(var(--color-info-rgb), 0.08)",
              border: "1px solid rgba(var(--color-info-rgb), 0.2)",
              color: "var(--bs-emphasis-color)",
            }}
          >
            Minimum Required: <strong className="fw-bold">{minRequired}</strong> Points
          </div>

          <div className="mb-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`form-control font-size-13 py-2 px-3 ${error ? "border-danger" : ""
                }`}
              style={{
                backgroundColor: "var(--bs-secondary-bg)",
                color: "var(--bs-emphasis-color)",
                borderColor: error
                  ? "var(--bs-danger)"
                  : "var(--bs-border-color-translucent)",
                borderRadius: "8px",
              }}
              placeholder="Enter Points"
              value={points}
              onKeyDown={handleKeyDown}
              onChange={handlePointsChange}
              disabled={isSubmitting}
            />
            {error && (
              <div className="text-danger font-size-11 mt-1 d-flex align-items-center gap-1">
                <i className="bx bx-error-circle"></i>
                <span>{error}</span>
              </div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 py-2 fw-semibold text-white shadow-sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
